import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Note as NotesIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PushPin as PinIcon,
  PushPinOutlined as UnpinIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Label as LabelIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Create as CreateIcon
} from '@mui/icons-material';
import { Project, User } from '../../types';
import { apiService } from '../../services/api';
import { toast } from '../UI/Toast';
import { UserAvatar } from '../UI/UserAvatar';

interface ProjectNote {
  id: string;
  project_id: string;
  title: string;
  content: string;
  content_type: string;
  tags: string;
  is_pinned: boolean;
  author_id: string;
  author: {
    id: string;
    display_name: string;
    email_address: string;
    avatar_url: string;
  };
  created_at: string;
  updated_at: string;
}

interface ProjectNotesProps {
  project: Project;
  currentUser: User;
}

export const ProjectNotes: React.FC<ProjectNotesProps> = ({
  project,
  currentUser
}) => {
  const [notes, setNotes] = useState<ProjectNote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingNote, setEditingNote] = useState<ProjectNote | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTags, setFilterTags] = useState('');
  const [showPinnedOnly, setShowPinnedOnly] = useState(false);
  const [selectedNoteMenu, setSelectedNoteMenu] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    tags: '',
    is_pinned: false
  });

  // Enhanced Quill editor configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'blockquote', 'code-block'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent', 'color', 'background',
    'align', 'link', 'blockquote', 'code-block'
  ];

  useEffect(() => {
    loadNotes();
  }, [project.id, showPinnedOnly]);

  const loadNotes = async () => {
    try {
      setIsLoading(true);
      const data = await apiService.getProjectNotes(project.id, { pinned_only: showPinnedOnly });
      setNotes(data);
    } catch (error) {
      console.error('Failed to load notes:', error);
      toast.error('Failed to load project notes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNote = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Note title is required');
      return;
    }

    try {
      toast.loading('Creating note...', { id: 'create-note' });
      
      const noteData = {
        project_id: project.id,
        title: formData.title,
        content: formData.content,
        content_type: 'html',
        tags: formData.tags,
        is_pinned: formData.is_pinned
      };

      const newNote = await apiService.createProjectNote(noteData);
      setNotes(prev => [newNote, ...prev]);
      
      // Reset form
      setFormData({
        title: '',
        content: '',
        tags: '',
        is_pinned: false
      });
      setShowCreateForm(false);
      
      toast.success('Note created successfully!', { id: 'create-note' });
    } catch (error) {
      console.error('Failed to create note:', error);
      toast.error('Failed to create note', { id: 'create-note' });
    }
  };

  const handleUpdateNote = async (noteId: string) => {
    if (!editingNote) return;

    try {
      toast.loading('Updating note...', { id: 'update-note' });
      
      const updates = {
        title: editingNote.title,
        content: editingNote.content,
        tags: editingNote.tags,
        is_pinned: editingNote.is_pinned
      };

      const updatedNote = await apiService.updateProjectNote(noteId, updates);
      setNotes(prev => prev.map(note => 
        note.id === noteId ? updatedNote : note
      ));
      
      setEditingNote(null);
      toast.success('Note updated successfully!', { id: 'update-note' });
    } catch (error) {
      console.error('Failed to update note:', error);
      toast.error('Failed to update note', { id: 'update-note' });
    }
  };

  const handleDeleteNote = async (noteId: string, noteTitle: string) => {
    if (!confirm(`Are you sure you want to delete "${noteTitle}"? This action cannot be undone.`)) return;

    try {
      toast.loading('Deleting note...', { id: 'delete-note' });
      
      await apiService.deleteProjectNote(noteId);
      setNotes(prev => prev.filter(note => note.id !== noteId));
      
      toast.success('Note deleted successfully!', { id: 'delete-note' });
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast.error('Failed to delete note', { id: 'delete-note' });
    }
  };

  const handleTogglePin = async (noteId: string) => {
    try {
      const result = await apiService.toggleNotePin(noteId);
      setNotes(prev => prev.map(note => 
        note.id === noteId ? { ...note, is_pinned: result.is_pinned } : note
      ));
      
      toast.success(`Note ${result.is_pinned ? 'pinned' : 'unpinned'} successfully!`);
    } catch (error) {
      console.error('Failed to toggle pin:', error);
      toast.error('Failed to update note pin status');
    }
  };

  const filteredNotes = notes.filter(note => {
    const matchesSearch = note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         note.content.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesTags = !filterTags || note.tags.toLowerCase().includes(filterTags.toLowerCase());
    
    return matchesSearch && matchesTags;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const stripHtml = (html: string) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const getTagsArray = (tagsString: string) => {
    return tagsString.split(',').map(tag => tag.trim()).filter(tag => tag);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6 mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg">
              <NotesIcon className="text-white" style={{ fontSize: 24 }} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Project Notes</h2>
              <p className="text-gray-600">Documentation and knowledge base for {project.name}</p>
            </div>
          </div>
          
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            <AddIcon style={{ fontSize: 20 }} />
            <span className="font-medium">New Note</span>
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-6">
          <div className="flex-1 relative">
            <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" style={{ fontSize: 20 }} />
            <input
              type="text"
              placeholder="Search notes by title or content..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="relative">
              <FilterIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" style={{ fontSize: 18 }} />
              <input
                type="text"
                placeholder="Filter by tags..."
                value={filterTags}
                onChange={(e) => setFilterTags(e.target.value)}
                className="pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent w-48"
              />
            </div>
            
            <label className="flex items-center space-x-3 bg-gray-50 px-4 py-3 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer">
              <input
                type="checkbox"
                checked={showPinnedOnly}
                onChange={(e) => setShowPinnedOnly(e.target.checked)}
                className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
              />
              <span className="text-sm font-medium text-gray-700">Pinned only</span>
            </label>
          </div>
        </div>
      </div>

      {/* Create Note Form */}
      {showCreateForm && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-lg mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-200 p-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                <CreateIcon className="text-white" style={{ fontSize: 20 }} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Create New Note</h3>
            </div>
          </div>
          
          <form onSubmit={handleCreateNote} className="p-6">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Note Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 text-lg font-medium"
                  placeholder="Enter a descriptive title for your note..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Content
                </label>
                <div className="border border-gray-300 rounded-xl overflow-hidden shadow-sm">
                  <ReactQuill
                    value={formData.content}
                    onChange={(content) => setFormData({ ...formData, content })}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Write your note content here... Use the toolbar above for formatting."
                    style={{ minHeight: '400px' }}
                    theme="snow"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Tags
                  </label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="meeting, requirements, design, documentation..."
                  />
                  <p className="text-xs text-gray-500 mt-2">Separate multiple tags with commas</p>
                </div>
                
                <div className="flex items-center justify-center">
                  <label className="flex items-center space-x-3 bg-yellow-50 border border-yellow-200 px-6 py-4 rounded-xl hover:bg-yellow-100 transition-colors cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_pinned}
                      onChange={(e) => setFormData({ ...formData, is_pinned: e.target.checked })}
                      className="rounded text-yellow-600 focus:ring-yellow-500 w-5 h-5"
                    />
                    <PinIcon className="text-yellow-600" style={{ fontSize: 20 }} />
                    <span className="text-sm font-medium text-yellow-800">Pin this note</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setFormData({ title: '', content: '', tags: '', is_pinned: false });
                }}
                className="flex items-center space-x-2 px-6 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-all duration-200"
              >
                <CancelIcon style={{ fontSize: 18 }} />
                <span className="font-medium">Cancel</span>
              </button>
              <button
                type="submit"
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <SaveIcon style={{ fontSize: 18 }} />
                <span className="font-medium">Create Note</span>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Notes List */}
      {isLoading ? (
        <div className="text-center py-16">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading notes...</p>
        </div>
      ) : filteredNotes.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <NotesIcon className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">No notes found</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            {notes.length === 0 
              ? 'Create your first project note to document important information, meeting notes, or project requirements.'
              : 'No notes match your current search and filter criteria. Try adjusting your filters.'
            }
          </p>
          {notes.length === 0 && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Create Your First Note
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {filteredNotes.map((note) => (
            <div key={note.id} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden">
              {editingNote?.id === note.id ? (
                // Edit Mode
                <div>
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 p-6">
                    <div className="flex items-center space-x-3">
                      <EditIcon className="text-blue-600" style={{ fontSize: 20 }} />
                      <h3 className="text-lg font-semibold text-gray-900">Editing Note</h3>
                    </div>
                  </div>
                  
                  <div className="p-6 space-y-6">
                    <input
                      type="text"
                      value={editingNote.title}
                      onChange={(e) => setEditingNote({ ...editingNote, title: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg font-semibold"
                      placeholder="Note title..."
                    />
                    
                    <div className="border border-gray-300 rounded-xl overflow-hidden">
                      <ReactQuill
                        value={editingNote.content}
                        onChange={(content) => setEditingNote({ ...editingNote, content })}
                        modules={quillModules}
                        formats={quillFormats}
                        style={{ minHeight: '400px' }}
                        theme="snow"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <input
                        type="text"
                        value={editingNote.tags}
                        onChange={(e) => setEditingNote({ ...editingNote, tags: e.target.value })}
                        className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Tags (comma separated)..."
                      />
                      
                      <label className="flex items-center space-x-3 bg-yellow-50 border border-yellow-200 px-6 py-3 rounded-xl hover:bg-yellow-100 transition-colors cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingNote.is_pinned}
                          onChange={(e) => setEditingNote({ ...editingNote, is_pinned: e.target.checked })}
                          className="rounded text-yellow-600 focus:ring-yellow-500 w-5 h-5"
                        />
                        <PinIcon className="text-yellow-600" style={{ fontSize: 20 }} />
                        <span className="text-sm font-medium text-yellow-800">Pin this note</span>
                      </label>
                    </div>
                    
                    <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
                      <button
                        onClick={() => setEditingNote(null)}
                        className="flex items-center space-x-2 px-6 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-all duration-200"
                      >
                        <CancelIcon style={{ fontSize: 18 }} />
                        <span className="font-medium">Cancel</span>
                      </button>
                      <button
                        onClick={() => handleUpdateNote(note.id)}
                        className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                      >
                        <SaveIcon style={{ fontSize: 18 }} />
                        <span className="font-medium">Save Changes</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // View Mode
                <div>
                  {/* Note Header */}
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          {note.is_pinned && (
                            <div className="flex items-center space-x-1 bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full">
                              <PinIcon style={{ fontSize: 16 }} />
                              <span className="text-xs font-medium">Pinned</span>
                            </div>
                          )}
                          <h3 className="text-xl font-bold text-gray-900">{note.title}</h3>
                        </div>
                        
                        <div className="flex items-center space-x-6 text-sm text-gray-500">
                          <div className="flex items-center space-x-2">
                            <UserAvatar
                              name={note.author.display_name}
                              src={note.author.avatar_url}
                              className="w-6 h-6 border-2 border-white shadow-sm"
                            />
                            <span className="font-medium">{note.author.display_name}</span>
                          </div>
                          
                          <div className="flex items-center space-x-1">
                            <ScheduleIcon style={{ fontSize: 16 }} />
                            <span>Updated {formatDate(note.updated_at)}</span>
                          </div>
                          
                          {note.tags && (
                            <div className="flex items-center space-x-1">
                              <LabelIcon style={{ fontSize: 16 }} />
                              <span>{getTagsArray(note.tags).length} tag{getTagsArray(note.tags).length !== 1 ? 's' : ''}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="relative">
                        <button
                          onClick={() => setSelectedNoteMenu(selectedNoteMenu === note.id ? null : note.id)}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <MoreVertIcon style={{ fontSize: 20 }} />
                        </button>
                        
                        {selectedNoteMenu === note.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
                            <button
                              onClick={() => {
                                handleTogglePin(note.id);
                                setSelectedNoteMenu(null);
                              }}
                              className="w-full text-left flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-yellow-50 hover:text-yellow-700 transition-colors"
                            >
                              {note.is_pinned ? (
                                <>
                                  <UnpinIcon style={{ fontSize: 16 }} />
                                  <span>Unpin Note</span>
                                </>
                              ) : (
                                <>
                                  <PinIcon style={{ fontSize: 16 }} />
                                  <span>Pin Note</span>
                                </>
                              )}
                            </button>
                            
                            <button
                              onClick={() => {
                                setEditingNote(note);
                                setSelectedNoteMenu(null);
                              }}
                              className="w-full text-left flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                            >
                              <EditIcon style={{ fontSize: 16 }} />
                              <span>Edit Note</span>
                            </button>
                            
                            <div className="border-t border-gray-100 my-1"></div>
                            
                            <button
                              onClick={() => {
                                handleDeleteNote(note.id, note.title);
                                setSelectedNoteMenu(null);
                              }}
                              className="w-full text-left flex items-center space-x-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                            >
                              <DeleteIcon style={{ fontSize: 16 }} />
                              <span>Delete Note</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Note Content */}
                  <div className="p-6">
                    <div 
                      dangerouslySetInnerHTML={{ __html: note.content }}
                      className="prose prose-sm max-w-none text-gray-700 leading-relaxed line-clamp-4"
                    />
                    
                    {/* Show "Read more" if content is long */}
                    {note.content.length > 300 && (
                      <button
                        onClick={() => {
                          // Toggle full content view
                          const contentDiv = document.querySelector(`[data-note-id="${note.id}"] .prose`);
                          if (contentDiv) {
                            contentDiv.classList.toggle('line-clamp-4');
                          }
                        }}
                        className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Read more →
                      </button>
                    )}
                  </div>
                  
                  {/* Note Footer */}
                  {note.tags && (
                    <div className="px-6 pb-6">
                      <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-100">
                        {getTagsArray(note.tags).map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                          >
                            <LabelIcon style={{ fontSize: 12 }} className="mr-1" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {notes.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{notes.length}</div>
              <div className="text-sm text-gray-500">Total Notes</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{notes.filter(n => n.is_pinned).length}</div>
              <div className="text-sm text-gray-500">Pinned</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{notes.filter(n => n.author_id === currentUser.id).length}</div>
              <div className="text-sm text-gray-500">By You</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {[...new Set(notes.flatMap(n => getTagsArray(n.tags)))].length}
              </div>
              <div className="text-sm text-gray-500">Unique Tags</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
